"use client";

import React, { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function BotanicalHeritage() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="86516" id="beyond-the-physical" className="dark bg-background text-foreground relative py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-20 mix-blend-overlay bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMDUiLz4KPHBhdGggZD0iTTAgMEw0IDRaIiBzdHJva2U9IiMwMDAiIHN0cm9rZS1vcGFjaXR5PSIwLjEiLz4KPC9zdmc+')]"></div>
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none bg-[radial-gradient(circle_at_0%_0%,_var(--color-primary)_0%,_transparent_50%)] opacity-5"></div>
      <div className="absolute bottom-0 right-0 w-full h-full pointer-events-none bg-[radial-gradient(circle_at_100%_100%,_var(--color-secondary)_0%,_transparent_50%)] opacity-10"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={leftRef} className={`lg:col-span-7 transition-all duration-1000 ease-out ${isLeftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="relative overflow-hidden shadow-2xl shadow-primary/20 border border-[#a855f7]/40 group">
              {}
              <div className="absolute inset-0 z-10 pointer-events-none"></div>
              
              <AspectRatio ratio={9 / 16} className="bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1624786/img/candlelit_yoga_dinner_cinematic.png" alt="Atmospheric candlelit yoga dinner with cinematic lighting" className="object-cover w-full h-full transition-transform duration-[20s] ease-linear group-hover:scale-110" />
                {}
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent z-10"></div>
                <div className="absolute bottom-8 left-8 z-20 flex items-center gap-3">
                  <span className="flex h-3 w-3 relative">
                    <span className="animate-pulse absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-lg h-3 w-3 bg-primary"></span>
                  </span>
                  <span className="text-primary text-sm tracking-[0.3em] uppercase font-default">
                    High Vibration
                  </span>
                </div>
              </AspectRatio>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 flex flex-col justify-center transition-all duration-1000 delay-300 ease-out ${isRightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="text-primary text-sm tracking-[0.4em] uppercase font-default font-semibold">
                Ritual & Rhythm
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-7xl font-default text-primary mb-8 leading-tight tracking-tight uppercase">
              The Divine <br />
              <span className="text-foreground">Communion</span>
            </h2>

            <div className="space-y-8">
              {}
              <div className="relative">
                <p className="text-lg text-foreground/90 leading-relaxed font-default tracking-wide">
                  At the intersection of ancient ceremony and modern pulse, we curate nights that transcend the ordinary. This is a sanctuary for the senses, where the vibration of deep bass meets the stillness of the soul.
                </p>
              </div>

              <div className="flex items-center gap-4 py-2">
                <Separator className="flex-1 bg-primary/20 h-[1px]" />
                <div className="w-2 h-2 rotate-45 bg-primary/40"></div>
                <Separator className="flex-1 bg-primary/20 h-[1px]" />
              </div>

              {}
              <div className="relative">
                <p className="text-lg text-foreground/90 leading-relaxed font-default tracking-wide">
                  Our candlelit tables are more than just dining; they are altars of connection. We feast on high-vibration nourishment, designed to fuel the dance and deepen the collective bond of our community.
                </p>
              </div>

              <div className="flex items-center gap-4 py-2">
                <Separator className="flex-1 bg-primary/20 h-[1px]" />
                <div className="w-2 h-2 rotate-45 bg-primary/40"></div>
                <Separator className="flex-1 bg-primary/20 h-[1px]" />
              </div>

              {}
              <div className="relative">
                <p className="text-lg text-foreground/90 leading-relaxed font-serif italic tracking-wider">
                  Step into the current. Feel the 126 BPM heartbeat of the room and awaken to a state of pure, cinematic presence that lingers long after the music stops.
                </p>
              </div>
            </div>

            <div className="mt-12">
              <Link to="#">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-8 text-xl uppercase tracking-widest transition-all hover:shadow-[0_0_40px_rgba(var(--color-primary),0.4)] font-default font-bold">
                  Join the Gathering
                </Button>
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>;
}